// What the public trust page shows (`/trust`), as the web build renders it from `docs/trust/` into
// `trust-content.json` (apps/web/trust/). Nothing here comes from a Tenant's data.

/** A section's legal review state, from `docs/trust/manifest.yaml`. A production Cell serves only reviewed ones. */
export type ReviewState = "draft" | "lawyer-reviewed";

export type Inline =
  | { readonly kind: "text"; readonly text: string }
  | { readonly kind: "strong"; readonly text: string }
  | { readonly kind: "link"; readonly text: string; readonly href: string };

export type Block =
  | { readonly kind: "heading"; readonly level: 3 | 4; readonly inlines: readonly Inline[] }
  | { readonly kind: "paragraph"; readonly inlines: readonly Inline[] }
  | { readonly kind: "list"; readonly items: readonly (readonly Inline[])[] };

export interface TrustSection {
  readonly id: string;
  readonly title: string;
  readonly review: ReviewState;
  readonly blocks: readonly Block[];
}

/** The recovery the latest passing restore drill achieved (ADR-0018), with what it tested and when. */
export interface RecoveryTargets {
  readonly drilledAt: string;
  readonly tested: string;
  readonly recoveryTimeMinutes: number;
  readonly recoveryPointMinutes: number;
}

/** An operator's short public note on a SEV-1 Incident, with no personal data. */
export interface IncidentNote {
  readonly date: string;
  readonly title: string;
  readonly postmortem: string;
  readonly blocks: readonly Block[];
}

/** One figure of a published transparency report: a count ("fewer than 5" for 1 to 4) or a few words. */
export interface ReportFigure {
  readonly label: string;
  readonly value: string;
}

/** One section of a published transparency report, as `pnpm transparency:report` writes it (#138). */
export interface ReportSection {
  readonly id: string;
  readonly title: string;
  /** Where the section's figures come from. */
  readonly source: string;
  readonly figures: readonly ReportFigure[];
  readonly table?: { readonly columns: readonly string[]; readonly rows: readonly (readonly string[])[] };
  readonly changes?: readonly { readonly date: string; readonly what: string }[];
  readonly note?: string;
}

/** A published transparency report (AUP cl. 17), from the JSON the CLI writes (#138). */
export interface TransparencyReport {
  readonly title: string;
  readonly publishedBy: string;
  /** Whole days in UTC, both included. */
  readonly from: string;
  readonly to: string;
  /** The day it was generated, YYYY-MM-DD in UTC. */
  readonly generatedOn: string;
  /** What LAMP follows ("aligned with"), and that it is not a certification. */
  readonly follows: string;
  /** How small counts are shown. */
  readonly smallCounts: string;
  /** How many businesses the figures were added up over. */
  readonly tenants: string;
  readonly sections: readonly ReportSection[];
}

export interface TrustContent {
  readonly sections: readonly TrustSection[];
  /** Absent when no restore drill has passed: then the page publishes no recovery targets. */
  readonly recovery?: RecoveryTargets;
  /** Newest first. */
  readonly incidents: readonly IncidentNote[];
  /** Every published report, newest first. */
  readonly reports: readonly TransparencyReport[];
}

/** Where the web build puts the page's content, and where the api's gate looks for the review states. */
export const TRUST_CONTENT_FILE = "trust-content.json";
export const TRUST_PATH = "/trust";
